/* eslint-disable import/extensions */
/* eslint-disable import/no-unresolved */
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  IconButton,
  SelectChangeEvent,
  Chip,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Search,
  Visibility,
  AssignmentTurnedIn,
  Clear,
  FilterList,
  List,
  PrecisionManufacturing,
  LocationOn,
} from '@mui/icons-material';

import api from '../../../../services/api';
import ErpAuthModal from '../../../../components/common/ErpAuthModal';
import GenericSearchInput from '../../../../components/common/GenericSearchInput';
import OpComponentDetailsModal, { OpComponentRowData } from '../../../../components/OpComponentDetailsModal';

interface Branch {
  id: number;
  nome: string;
}

interface Company {
  id: number;
  nome: string;
  subtitulo: string;
  filiais: Branch[];
}

export interface DischargeResult {
  op: number;
  codComponente: string;
  descricaoComponente?: string;
  quantidadeBaixada: number;
  status: 'success' | 'error';
  message: string;
}

const STORAGE_KEY = 'op_components_discharge_filters';

const formatNumberPtBR = (num?: number) => {
  if (num === undefined || num === null || Number.isNaN(Number(num))) return '0';
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
  }).format(Number(num));
};

function HighlightText({ text, highlight }: { text: string; highlight: string }) {
  const theme = useTheme();

  if (!highlight || !highlight.trim() || !text) {
    return <>{text}</>;
  }

  const tokens = highlight.trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return <>{text}</>;

  const escapedTokens = tokens.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const regex = new RegExp(`(${escapedTokens.join('|')})`, 'gi');
  const parts = String(text).split(regex);

  return (
    <>
      {parts.map((part, index) => {
        const isMatch = tokens.some(t => t.toLowerCase() === part.toLowerCase());
        return isMatch ? (
          <Box
            key={index}
            component="span"
            sx={{
              bgcolor: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.45 : 0.35),
              color: 'text.primary',
              fontWeight: 'bold',
              px: 0.3,
              borderRadius: '2px',
            }}
          >
            {part}
          </Box>
        ) : (
          part
        );
      })}
    </>
  );
}

export default function OpComponentsDischargeManager() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const savedFilters = useMemo(() => {
    try {
      const item = localStorage.getItem(STORAGE_KEY);
      return item ? JSON.parse(item) : null;
    } catch {
      return null;
    }
  }, []);

  // FILTROS
  const [companyId, setCompanyId] = useState<number>(savedFilters?.companyId ?? 1);
  const [branchId, setBranchId] = useState<number>(savedFilters?.branchId ?? 1);
  const [origin, setOrigin] = useState<string>(savedFilters?.origin ?? '');
  const [opNum, setOpNum] = useState<string>(savedFilters?.opNum ?? '');
  const [stage, setStage] = useState<string>(savedFilters?.stage ?? '');
  const [onlyTransfer, setOnlyTransfer] = useState<boolean>(savedFilters?.onlyTransfer ?? false);
  const [status, setStatus] = useState<string>(savedFilters?.status ?? 'PEN');
  const [sortMode, setSortMode] = useState<string>(savedFilters?.sortMode ?? 'ORIGEM_OP_END');
  const [quickSearch, setQuickSearch] = useState<string>('');

  const [components, setComponents] = useState<OpComponentRowData[]>([]);
  const [companyList, setCompanyList] = useState<Company[]>([]);
  const [selectedItems, setSelectedItems] = useState<OpComponentRowData[]>([]);
  const [hasSearched, setHasSearched] = useState<boolean>(false);

  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState<boolean>(true);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  // MODAIS
  const [authModalOpen, setAuthModalOpen] = useState<boolean>(false);
  const [detailItem, setDetailItem] = useState<OpComponentRowData | null>(null);

  // MODAL DE ATENDIMENTO
  const [attendanceTarget, setAttendanceTarget] = useState<OpComponentRowData[] | null>(null);
  const [globalTransaction, setGlobalTransaction] = useState<string>('90265');
  const [dischargeForm, setDischargeForm] = useState<{
    [key: string]: { qtd: number; lote: string; deposito: string; transacao: string };
  }>({});

  const [resultModal, setResultModal] = useState<{
    open: boolean;
    results: DischargeResult[];
  }>({
    open: false,
    results: [],
  });

  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    type: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    type: 'info',
  });

  const showNotification = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    setNotification({ open: true, message, type });
  };

  const getItemKey = (item: OpComponentRowData) =>
    `${item.empresa}-${item.filial}-${item.op}-${item.estagio}-${item.codComponente}-${item.codDerivacao}`;

  const executeFilterSearch = async (cId = companyId, bId = branchId) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          companyId: cId,
          branchId: bId,
          origin,
          opNum,
          stage,
          onlyTransfer,
          status,
          sortMode,
        }),
      );
    } catch (e) {
      console.error('Erro ao salvar filtros:', e);
    }

    try {
      setIsLoading(true);
      setSelectedItems([]);
      const { data } = await api.get<OpComponentRowData[]>('erp/production/op-components', {
        params: {
          companyId: cId,
          branchId: bId,
          origin: origin.trim() || undefined,
          opNum: opNum.trim() || undefined,
          stage: stage.trim() || undefined,
          onlyTransfer: onlyTransfer ? 'true' : 'false',
          status,
          sortMode,
        },
      });
      setComponents(data || []);
      setPage(0);
      setHasSearched(true);
    } catch (error: any) {
      console.error('Erro ao buscar componentes de OP:', error);
      showNotification(error?.response?.data?.message || 'Erro ao carregar componentes.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        setIsLoadingCompanies(true);
        const { data } = await api.get<Company[]>('app/data-collection/get-company');
        if (data && Array.isArray(data) && data.length > 0) {
          setCompanyList(data);
          const targetCompanyId =
            savedFilters?.companyId && data.some(c => c.id === savedFilters.companyId)
              ? savedFilters.companyId
              : data[0].id;
          const found = data.find(c => c.id === targetCompanyId) || data[0];
          const targetBranchId =
            savedFilters?.branchId && found.filiais?.some(f => f.id === savedFilters.branchId)
              ? savedFilters.branchId
              : (found.filiais?.[0]?.id ?? 1);

          setCompanyId(targetCompanyId);
          setBranchId(targetBranchId);
          executeFilterSearch(targetCompanyId, targetBranchId);
        }
      } catch (err) {
        console.error('Erro ao carregar empresas:', err);
      } finally {
        setIsLoadingCompanies(false);
      }
    };
    fetchCompanies();
  }, []);

  const currentCompany = useMemo(() => {
    return companyList.find(e => e.id === companyId) || companyList[0] || { filiais: [] };
  }, [companyList, companyId]);

  const filteredComponents = useMemo(() => {
    if (!quickSearch.trim()) return components;
    const term = quickSearch.toLowerCase();
    return components.filter(c => {
      return (
        String(c.op).includes(term) ||
        c.codOrigem.toLowerCase().includes(term) ||
        c.descricaoOrigem.toLowerCase().includes(term) ||
        c.codComponente.toLowerCase().includes(term) ||
        c.descricaoComponente.toLowerCase().includes(term) ||
        String(c.pedido || '').includes(term) ||
        c.enderecamento.toLowerCase().includes(term) ||
        c.codDeposito.toLowerCase().includes(term)
      );
    });
  }, [components, quickSearch]);

  const paginatedComponents = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredComponents.slice(start, start + rowsPerPage);
  }, [filteredComponents, page, rowsPerPage]);

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      const selectable = filteredComponents.filter(c => c.quantidadeAUtilizar > 0);
      setSelectedItems(selectable);
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectItem = (item: OpComponentRowData) => {
    const key = getItemKey(item);
    if (selectedItems.some(s => getItemKey(s) === key)) {
      setSelectedItems(selectedItems.filter(s => getItemKey(s) !== key));
    } else {
      setSelectedItems([...selectedItems, item]);
    }
  };

  const handleOpenAttendanceModal = (items: OpComponentRowData[]) => {
    const initialForm: {
      [key: string]: { qtd: number; lote: string; deposito: string; transacao: string };
    } = {};
    items.forEach(it => {
      const key = getItemKey(it);
      initialForm[key] = {
        qtd: it.quantidadeAUtilizar,
        lote: '',
        deposito: '', // Sempre traz vazio conforme solicitado
        transacao: globalTransaction,
      };
    });
    setDischargeForm(initialForm);
    setAttendanceTarget(items);
  };

  const handleApplyGlobalTransaction = (newTrans: string) => {
    setGlobalTransaction(newTrans);
    setDischargeForm(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(k => {
        updated[k] = { ...updated[k], transacao: newTrans };
      });
      return updated;
    });
  };

  const handleConfirmDischarge = async () => {
    if (!attendanceTarget || isProcessing) return;

    const payloadItems = attendanceTarget.map(it => {
      const key = getItemKey(it);
      const form = dischargeForm[key] || {
        qtd: it.quantidadeAUtilizar,
        lote: '',
        deposito: '',
        transacao: globalTransaction,
      };
      return {
        ...it,
        qtdUtilizar: form.qtd,
        lote: form.lote,
        deposito: form.deposito || it.codDeposito,
        transacao: form.transacao || globalTransaction,
      };
    });

    setIsProcessing(true);

    try {
      const { data } = await api.post('erp/production/op-components/discharge', {
        items: payloadItems,
      });

      setAttendanceTarget(null);
      setResultModal({
        open: true,
        results: data.results || [],
      });
      await executeFilterSearch();
    } catch (error: any) {
      console.error('Erro ao processar baixa de componentes:', error);
      const statusErr = error?.response?.status;
      const msg = error?.response?.data?.message || 'Erro ao processar baixa no ERP Sapiens.';

      if (statusErr === 401 || msg.toLowerCase().includes('credenciais') || msg.toLowerCase().includes('erp_login')) {
        setAttendanceTarget(null);
        showNotification('Autenticação do ERP necessária.', 'error');
        setAuthModalOpen(true);
        return;
      }

      setAttendanceTarget(null);
      setResultModal({
        open: true,
        results: error?.response?.data?.results || [],
      });
      await executeFilterSearch();
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Box pb={3}>
      <Box display="flex" alignItems="center" gap={1} mb={1.5}>
        <PrecisionManufacturing color="inherit" fontSize="large" />
        <Typography variant="h5" fontWeight="bold" color="text.primary">
          Baixa de Componentes de Estoque (OP) - F900BAC
        </Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" mb={3}>
        Consulte os componentes previstos de Ordens de Produção e execute o atendimento/baixa no ERP Senior Sapiens.
      </Typography>

      {/* PAINEL DE FILTROS */}
      <Paper elevation={2} sx={{ p: 2.5, mb: 3, borderRadius: 2, bgcolor: 'background.paper' }}>
        <Box display="flex" alignItems="center" gap={1} mb={2.5}>
          <FilterList color="primary" fontSize="small" />
          <Typography variant="subtitle2" fontWeight="bold" color="primary">
            Filtros da Ordem de Produção
          </Typography>
        </Box>

        <Grid container spacing={2} alignItems="center">
          {/* Empresa */}
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="select-comp-label">Empresa</InputLabel>
              <Select
                labelId="select-comp-label"
                value={companyId}
                label="Empresa"
                onChange={(e: SelectChangeEvent<number>) => {
                  const id = Number(e.target.value);
                  setCompanyId(id);
                  const f = companyList.find(c => c.id === id);
                  if (f?.filiais?.length) setBranchId(f.filiais[0].id);
                  setComponents([]);
                  setHasSearched(false);
                }}
                disabled={isLoadingCompanies}
              >
                {companyList.map(c => (
                  <MenuItem key={c.id} value={c.id}>
                    [{c.id}] {c.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Filial */}
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="select-fil-label">Filial</InputLabel>
              <Select
                labelId="select-fil-label"
                value={branchId}
                label="Filial"
                onChange={(e: SelectChangeEvent<number>) => {
                  setBranchId(Number(e.target.value));
                  setComponents([]);
                  setHasSearched(false);
                }}
                disabled={isLoadingCompanies || !currentCompany.filiais.length}
              >
                {currentCompany.filiais?.map(fil => (
                  <MenuItem key={fil.id} value={fil.id}>
                    [{fil.id}] {fil.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Origem */}
          <Grid item xs={12} sm={3}>
            <GenericSearchInput
              label="Origem da OP"
              source="origins"
              companyId={companyId}
              branchId={branchId}
              value={origin}
              onChange={val => {
                setOrigin(val);
                setComponents([]);
                setHasSearched(false);
              }}
              multiple
              placeholder="Ex: CALD, USIN"
            />
          </Grid>

          {/* Número da OP */}
          <Grid item xs={12} sm={3}>
            <GenericSearchInput
              label="Nº da Ordem de Produção (OP)"
              source="productionOrders"
              companyId={companyId}
              branchId={branchId}
              origin={origin}
              value={opNum}
              onChange={val => {
                setOpNum(val);
                setComponents([]);
                setHasSearched(false);
              }}
              multiple
              placeholder={origin ? `Ex: OPs da origem ${origin}` : 'Ex: 19575, 19580-19590'}
            />
          </Grid>

          {/* Estágio */}
          <Grid item xs={12} sm={3}>
            <GenericSearchInput
              label="Estágio de Produção"
              source="stages"
              companyId={companyId}
              branchId={branchId}
              value={stage}
              onChange={val => {
                setStage(val);
                setComponents([]);
                setHasSearched(false);
              }}
              multiple
              placeholder="Ex: 10, 20"
            />
          </Grid>

          {/* Situação: Pendentes / Baixados / Todos */}
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="select-status-label">Situação</InputLabel>
              <Select
                labelId="select-status-label"
                value={status}
                label="Situação"
                onChange={e => {
                  setStatus(e.target.value);
                  setComponents([]);
                  setHasSearched(false);
                }}
              >
                <MenuItem value="PEN">Pendentes de Baixa</MenuItem>
                <MenuItem value="BAI">Totalmente Baixados</MenuItem>
                <MenuItem value="ALL">Todos os Componentes</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Ordenação da Lista */}
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="select-sort-label">Ordenação da Lista</InputLabel>
              <Select
                labelId="select-sort-label"
                value={sortMode}
                label="Ordenação da Lista"
                onChange={e => {
                  setSortMode(e.target.value);
                  setComponents([]);
                  setHasSearched(false);
                }}
              >
                <MenuItem value="ORIGEM_OP_END">Origem / OP / Endereço</MenuItem>
                <MenuItem value="END_ORIGEM_OP">Endereço / Origem / OP</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Checkbox Apenas Transferência */}
          <Grid item xs={12} sm={3}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={onlyTransfer}
                  onChange={e => {
                    setOnlyTransfer(e.target.checked);
                    setComponents([]);
                    setHasSearched(false);
                  }}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2" fontWeight="500">
                  Apenas Transferência
                </Typography>
              }
            />
          </Grid>

          {/* Filtro Rápido na Tabela */}
          <Grid item xs={12} sm={9}>
            <TextField
              fullWidth
              size="small"
              label="Filtro Rápido na Tela"
              placeholder="Filtre por componente, descrição, OP, pedido, endereço..."
              value={quickSearch}
              onChange={e => {
                setQuickSearch(e.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: quickSearch ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setQuickSearch('')}>
                      <Clear fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          </Grid>

          {/* Botão Pesquisar */}
          <Grid item xs={12} sm={3} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={() => executeFilterSearch()}
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : <Search />}
              sx={{ height: 40, fontWeight: 'bold' }}
            >
              Pesquisar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* AÇÕES EM LOTE */}
      {selectedItems.length > 0 && (
        <Paper
          elevation={3}
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2,
            bgcolor: isDark ? alpha(theme.palette.primary.main, 0.2) : alpha(theme.palette.primary.light, 0.2),
            border: `1px solid ${theme.palette.primary.main}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold">
            {selectedItems.length} componente(s) selecionado(s) para baixa
          </Typography>
          <Button
            variant="contained"
            color="success"
            startIcon={<AssignmentTurnedIn />}
            onClick={() => handleOpenAttendanceModal(selectedItems)}
            sx={{ fontWeight: 'bold' }}
          >
            Atender Selecionados
          </Button>
        </Paper>
      )}

      {/* TABELA DE COMPONENTES */}
      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box p={2} display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <List color="primary" fontSize="small" />
            <Typography variant="subtitle2" fontWeight="bold" color="primary">
              Componentes para Baixa ({filteredComponents.length})
            </Typography>
          </Box>
        </Box>

        <TableContainer sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ '& th': { bgcolor: isDark ? 'grey.900' : 'grey.100', fontWeight: 'bold' } }}>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={
                      selectedItems.length > 0 &&
                      selectedItems.length < filteredComponents.filter(c => c.quantidadeAUtilizar > 0).length
                    }
                    checked={
                      filteredComponents.filter(c => c.quantidadeAUtilizar > 0).length > 0 &&
                      selectedItems.length === filteredComponents.filter(c => c.quantidadeAUtilizar > 0).length
                    }
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>Origem</TableCell>
                <TableCell>OP</TableCell>
                <TableCell>Pedido</TableCell>
                <TableCell>Estágio</TableCell>
                <TableCell>Componente / Derivação</TableCell>
                <TableCell align="center">U.M.</TableCell>
                <TableCell>Depósito</TableCell>
                <TableCell align="right">Qtd. a Utilizar</TableCell>
                <TableCell>Endereçamento</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 5 }}>
                    <CircularProgress />
                    <Typography variant="body2" color="text.secondary" mt={1}>
                      Carregando componentes...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : !hasSearched && components.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 5 }}>
                    <Typography variant="body1" color="text.secondary">
                      Selecione os filtros e clique no botão <b>&quot;Pesquisar&quot;</b> para consultar.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : paginatedComponents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 5 }}>
                    <Typography variant="body1" color="text.secondary">
                      Nenhum componente encontrado para os filtros aplicados.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedComponents.map(row => {
                  const key = getItemKey(row);
                  const isSelected = selectedItems.some(s => getItemKey(s) === key);
                  const isPending = row.quantidadeAUtilizar > 0;

                  return (
                    <TableRow key={key} hover selected={isSelected}>
                      <TableCell padding="checkbox">
                        <Checkbox checked={isSelected} disabled={!isPending} onChange={() => handleSelectItem(row)} />
                      </TableCell>

                      <TableCell sx={{ fontWeight: 'bold' }}>
                        <HighlightText text={row.codOrigem} highlight={quickSearch} />
                      </TableCell>

                      <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                        <HighlightText text={String(row.op)} highlight={quickSearch} />
                      </TableCell>

                      <TableCell>{row.pedido ? `#${row.pedido}` : '-'}</TableCell>

                      <TableCell>
                        {row.estagio} - {row.descricaoEstagio}
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          [<HighlightText text={row.codComponente} highlight={quickSearch} />]{' '}
                          <HighlightText text={row.descricaoComponente} highlight={quickSearch} />
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Derivação: {row.codDerivacao} ({row.descricaoDerivacao})
                        </Typography>
                      </TableCell>

                      <TableCell align="center">{row.unidadeMedida}</TableCell>

                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {row.codDeposito}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row.descricaoDeposito}
                        </Typography>
                      </TableCell>

                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          color={isPending ? 'warning.main' : 'success.main'}
                        >
                          {formatNumberPtBR(row.quantidadeAUtilizar)}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <LocationOn fontSize="small" color="action" />
                          <Typography variant="body2" fontWeight="500">
                            {row.enderecamento}
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell align="center">
                        <Box display="flex" justifyContent="center" gap={0.5}>
                          <Tooltip title="Ver Detalhes">
                            <IconButton size="small" onClick={() => setDetailItem(row)}>
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          {isPending && (
                            <Tooltip title="Atender / Baixar Componente">
                              <IconButton size="small" color="success" onClick={() => handleOpenAttendanceModal([row])}>
                                <AssignmentTurnedIn fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredComponents.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={e => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage="Itens por página:"
        />
      </Paper>

      {/* MODAL REUTILIZÁVEL DE DETALHES */}
      <OpComponentDetailsModal open={Boolean(detailItem)} onClose={() => setDetailItem(null)} item={detailItem} />

      {/* MODAL DE ATENDIMENTO / BAIXA DE COMPONENTES */}
      <Dialog
        open={Boolean(attendanceTarget)}
        onClose={() => !isProcessing && setAttendanceTarget(null)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            width: { xs: '98vw', sm: '95vw', md: '90vw' },
            maxWidth: '1500px',
            borderRadius: 2.5,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>Atendimento de Componente(s) - Baixa de Estoque</DialogTitle>
        <DialogContent dividers>
          {isProcessing ? (
            <Box display="flex" alignItems="center" gap={2} p={2} mb={2} bgcolor="info.50" borderRadius={1.5}>
              <CircularProgress size={24} />
              <Typography variant="subtitle2" fontWeight="bold">
                Processando baixa no ERP Senior Sapiens...
              </Typography>
            </Box>
          ) : (
            <Box mb={2}>
              <DialogContentText mb={2}>
                Defina a <b>Transação de Baixa</b> e selecione o <b>Lote</b> e o <b>Depósito</b> para cada item:
              </DialogContentText>

              {/* Transação Geral do Atendimento */}
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: isDark ? 'grey.900' : 'grey.50' }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6}>
                    <GenericSearchInput
                      label="Transação de Baixa (Geral)"
                      source="transactions"
                      companyId={companyId}
                      branchId={branchId}
                      module="EST"
                      value={globalTransaction}
                      onChange={val => handleApplyGlobalTransaction(val)}
                      placeholder="Ex: 90265"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      * Define a transação padrão de estoque aplicada aos itens que não tiverem transação customizada.
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          )}

          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ bgcolor: isDark ? 'grey.900' : 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 'bold', width: 100 }}>OP</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 220 }}>Componente</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: 220 }}>Depósito</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: 250 }}>Lote</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: 180 }}>Transação</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', width: 140 }}>
                    Qtd a Utilizar
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {attendanceTarget?.map(item => {
                  const key = getItemKey(item);
                  const currentForm = dischargeForm[key] || {
                    qtd: item.quantidadeAUtilizar,
                    lote: '',
                    deposito: '',
                    transacao: globalTransaction,
                  };

                  return (
                    <TableRow key={key}>
                      <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>#{item.op}</TableCell>

                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          [{item.codComponente}]
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {item.descricaoComponente}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Derivação: <b>{item.codDerivacao}</b>
                        </Typography>
                      </TableCell>

                      {/* Depósito via GenericSearchInput (Traz vazio) */}
                      <TableCell>
                        <GenericSearchInput
                          label="Depósito"
                          source="deposits"
                          companyId={companyId}
                          branchId={branchId}
                          value={currentForm.deposito}
                          disabled={isProcessing}
                          placeholder="Selecione o depósito"
                          onChange={val => {
                            setDischargeForm(prev => ({
                              ...prev,
                              [key]: { ...prev[key], deposito: val },
                            }));
                          }}
                        />
                      </TableCell>

                      {/* Lote via GenericSearchInput (Traz saldo de todos os depósitos se vazio, e preenche o depósito) */}
                      <TableCell>
                        <GenericSearchInput
                          label="Lote de Estoque"
                          source="lots"
                          companyId={companyId}
                          branchId={branchId}
                          productId={item.codComponente}
                          derivationId={item.codDerivacao}
                          depositId={currentForm.deposito}
                          multiple={false}
                          value={currentForm.lote}
                          disabled={isProcessing}
                          placeholder={item.controlaLote === 'S' ? 'Obrigatório (Lote)' : 'Opcional (Lote)'}
                          onChange={(val, selected) => {
                            const selectedLot = selected && selected[0];
                            setDischargeForm(prev => {
                              const currentDep = prev[key]?.deposito || '';
                              // Se o depósito estiver vazio e o lote selecionado tiver um depósito com saldo, preenche automaticamente
                              const autoDeposit =
                                !currentDep && selectedLot?.depositoOrigem ? selectedLot.depositoOrigem : currentDep;

                              return {
                                ...prev,
                                [key]: {
                                  ...prev[key],
                                  lote: val,
                                  deposito: autoDeposit,
                                },
                              };
                            });
                          }}
                        />
                      </TableCell>

                      {/* Transação via GenericSearchInput */}
                      <TableCell>
                        <GenericSearchInput
                          label="Transação"
                          source="transactions"
                          companyId={companyId}
                          branchId={branchId}
                          module="EST"
                          value={currentForm.transacao}
                          disabled={isProcessing}
                          placeholder="Ex: 90265"
                          onChange={val => {
                            setDischargeForm(prev => ({
                              ...prev,
                              [key]: { ...prev[key], transacao: val },
                            }));
                          }}
                        />
                      </TableCell>

                      {/* Quantidade a Utilizar */}
                      <TableCell align="right">
                        <TextField
                          size="small"
                          type="number"
                          value={currentForm.qtd}
                          disabled={isProcessing}
                          inputProps={{ min: 0.0001, step: 'any' }}
                          onChange={e => {
                            setDischargeForm(prev => ({
                              ...prev,
                              [key]: { ...prev[key], qtd: Number(e.target.value) },
                            }));
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setAttendanceTarget(null)} color="inherit" disabled={isProcessing}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmDischarge}
            variant="contained"
            color="success"
            disabled={isProcessing}
            startIcon={isProcessing ? <CircularProgress size={18} color="inherit" /> : <AssignmentTurnedIn />}
            sx={{ fontWeight: 'bold' }}
          >
            {isProcessing ? 'Processando...' : 'Confirmar Atendimento'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* MODAL DE RESULTADOS */}
      <Dialog
        open={resultModal.open}
        onClose={() => setResultModal(prev => ({ ...prev, open: false }))}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
          <AssignmentTurnedIn color="primary" /> Resultado do Atendimento no ERP
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2 }}>
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 350 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ bgcolor: isDark ? 'grey.900' : 'grey.200' }}>
                  <TableCell>OP</TableCell>
                  <TableCell>Componente</TableCell>
                  <TableCell>Qtd Baixada</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Mensagem ERP</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {resultModal.results.map((r, idx) => (
                  <TableRow key={`${r.op}-${r.codComponente}-${idx}`} hover>
                    <TableCell sx={{ fontWeight: 'bold' }}>#{r.op}</TableCell>
                    <TableCell>{r.codComponente}</TableCell>
                    <TableCell>{formatNumberPtBR(r.quantidadeBaixada)}</TableCell>
                    <TableCell>
                      <Chip
                        label={r.status === 'success' ? 'Sucesso' : 'Erro'}
                        color={r.status === 'success' ? 'success' : 'error'}
                        size="small"
                        sx={{ fontWeight: 'bold' }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: r.status === 'success' ? 'text.secondary' : 'error.main' }}>
                      {r.message}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setResultModal(prev => ({ ...prev, open: false }))}
            variant="contained"
            color="primary"
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      <ErpAuthModal
        open={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        onSuccess={() => {
          showNotification('Autenticado com sucesso!', 'success');
          executeFilterSearch();
        }}
      />

      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={notification.type} variant="filled" sx={{ fontWeight: 'bold' }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
